<template>
  <article class="rounded-2xl border border-foxy-border bg-foxy-surface/80 p-4 text-foxy-text shadow-sm transition-colors sm:p-5"
    :class="!isEditing ? 'cursor-pointer hover:border-foxy-action/40 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20' : ''"
    :role="!isEditing && quoteUrl ? 'link' : undefined" :tabindex="!isEditing && quoteUrl ? 0 : undefined"
    :aria-label="!isEditing ? `Відкрити цитату з книги «${quote.book?.title || 'Книга'}»` : undefined"
    data-account-quote-card @click="navigateQuote" @keydown="handleCardKeydown">
    <header class="flex items-start gap-3">
      <a v-if="bookUrl" :href="bookUrl" class="shrink-0 rounded focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20">
        <img :src="resolveBookCover(quote.book && quote.book.cover)" :alt="quote.book ? quote.book.title : 'Книга'"
          class="h-14 w-10 rounded border border-foxy-border object-cover" loading="lazy" decoding="async" @error="onCoverError">
      </a>
      <div class="min-w-0 flex-1">
        <a v-if="bookUrl" :href="bookUrl" class="break-words text-sm font-bold text-foxy-text hover:text-foxy-action">{{ quote.book.title }}</a>
        <p class="mt-1 flex flex-wrap items-center gap-2 text-xs text-foxy-text-secondary">
          <span>{{ quote.displayed_at_human || formatDate(quote.displayed_at) }}</span>
          <span v-if="quote.is_private" class="acc-pill !px-2 !py-0.5">Приватна</span>
        </p>
      </div>
      <div v-if="quote.can_manage && !isEditing" class="flex shrink-0 flex-col gap-1.5 sm:flex-row sm:gap-2">
        <button type="button" class="acc-btn !px-2.5 !py-1.5 text-xs" @click="startEdit">Редагувати</button>
        <button type="button" class="acc-btn !px-2.5 !py-1.5 text-xs !text-foxy-error" :disabled="isDeleting" @click="deleteQuote">
          {{ isDeleting ? 'Видалення…' : 'Видалити' }}
        </button>
      </div>
    </header>

    <div v-if="isEditing" class="mt-4">
      <label :for="`quote-content-${quote.id}`" class="sr-only">Текст цитати</label>
      <textarea :id="`quote-content-${quote.id}`" v-model="editContent" class="foxy-textarea !min-h-[10rem] !w-full !resize-y !p-3"
        maxlength="1000" placeholder="Введіть текст цитати..." />
      <div class="mt-1 flex items-center justify-between gap-3 text-xs text-foxy-text-secondary">
        <span>До 1000 символів</span><span>{{ editContent.length }} / 1000</span>
      </div>
      <label :for="`quote-page-${quote.id}`" class="mt-3 block text-sm font-semibold">Сторінка — необов’язково</label>
      <input :id="`quote-page-${quote.id}`" v-model="editPageNumber" type="number" min="1" class="foxy-input mt-1 w-full" placeholder="Наприклад, 42">
      <fieldset class="mt-3 space-y-2">
        <legend class="text-sm font-semibold">Видимість</legend>
        <label class="flex items-start gap-2 text-sm text-foxy-text-secondary">
          <input v-model="editIsPublic" type="radio" :value="true" class="mt-1">
          <span><strong class="text-foxy-text">Публічна</strong> — бачитимуть інші читачі</span>
        </label>
        <label class="flex items-start gap-2 text-sm text-foxy-text-secondary">
          <input v-model="editIsPublic" type="radio" :value="false" class="mt-1">
          <span><strong class="text-foxy-text">Приватна</strong> — бачитимете лише ви</span>
        </label>
      </fieldset>
      <p v-if="editError" class="mt-2 text-sm text-foxy-error" role="alert">{{ editError }}</p>
      <div class="mt-3 flex flex-wrap gap-2">
        <button type="button" class="acc-btn-primary" :disabled="isSaving" @click="saveEdit">{{ isSaving ? 'Збереження…' : 'Зберегти' }}</button>
        <button type="button" class="acc-btn" :disabled="isSaving" @click="cancelEdit">Скасувати</button>
      </div>
    </div>
    <div v-else class="mt-4 border-l-2 border-foxy-action/45 pl-4 sm:pl-5">
      <blockquote ref="content" class="max-w-[72ch] whitespace-pre-line break-words text-lg font-normal italic leading-relaxed text-foxy-text"
        :class="{ 'quote-clamped': !isExpanded }">{{ quote.content }}</blockquote>
      <button v-if="isExpanded || isOverflowing" type="button" class="mt-2 text-sm font-semibold text-foxy-action hover:underline" @click="toggleExpanded">
        {{ isExpanded ? 'Згорнути' : 'Розгорнути' }}
      </button>
      <p v-if="quote.page_number" class="mt-3 text-xs text-foxy-text-secondary">Сторінка {{ quote.page_number }}</p>
    </div>

    <footer class="mt-4 flex flex-wrap items-center gap-4 text-sm text-foxy-text-secondary">
      <button v-if="quote.can_like" type="button" class="inline-flex min-h-9 items-center gap-1.5 rounded hover:text-foxy-text disabled:cursor-wait"
        :aria-label="isLiked ? 'Прибрати вподобання' : 'Вподобати цитату'" :aria-pressed="isLiked ? 'true' : 'false'"
        :aria-busy="isLikePending ? 'true' : 'false'" :disabled="isLikePending" @click="toggleLike">
        <i class="fas fa-heart" :class="isLiked ? 'text-red-500 dark:text-red-400' : 'text-foxy-text-secondary'" aria-hidden="true"></i>
        {{ likesCount }}
      </button>
      <span v-else class="inline-flex min-h-9 items-center gap-1.5" aria-label="Кількість вподобань">
        <i class="fas fa-heart text-foxy-text-secondary" aria-hidden="true"></i>{{ likesCount }}
      </span>
      <button v-if="quote.can_favorite" type="button" class="inline-flex min-h-9 items-center gap-1.5 rounded hover:text-foxy-text disabled:cursor-wait disabled:opacity-60"
        :aria-pressed="isFavorited ? 'true' : 'false'" :aria-busy="isFavoritePending ? 'true' : 'false'"
        :aria-label="isFavorited ? 'Прибрати зі збереженого' : 'Зберегти'" :title="isFavorited ? 'Прибрати зі збереженого' : 'Зберегти'"
        :disabled="isFavoritePending" @click="toggleFavorite">
        <i :class="[isFavorited ? 'fas' : 'far', 'fa-bookmark', isFavorited ? 'text-foxy-text' : 'text-foxy-text-secondary']" aria-hidden="true"></i>
        <span>{{ isFavorited ? 'Збережено' : 'Зберегти' }}</span>
      </button>
      <report-button v-if="quote.can_report" class="ml-auto" :comfortable-trigger="true" :reportable-type="'App\\Models\\Quote'" :reportable-id="quote.id"
        :content-preview="String(quote.content || '').slice(0, 100)" :content-url="quoteUrl" />
    </footer>
  </article>
</template>


<script lang="js">
import axios from 'axios';

export default {
  name: 'AccountQuoteCard',
  props: { quote: { type: Object, required: true }, publicationContext: { type: Object, default: null } },
  data() {
    return {
      isExpanded: false, isOverflowing: false, isEditing: false,
      editContent: '', editPageNumber: null, editIsPublic: false, editError: '',
      isSaving: false, isDeleting: false,
      isLiked: Boolean(this.quote.is_liked_by_current_user),
      likesCount: Number(this.quote.likes_count || 0),
      isFavorited: Boolean(this.quote.is_favorited_by_current_user),
      isFavoritePending: false, isLikePending: false,
    };
  },
  computed: {
    bookUrl() { return this.quote?.book?.slug ? `/books/${this.quote.book.slug}` : ''; },
    bookQuotesUrl() { return this.quote?.book?.slug ? `/books/${this.quote.book.slug}/quotes` : ''; },
    quoteUrl() { return this.bookQuotesUrl ? `${this.bookQuotesUrl}?quote=${this.quote.id}#quote-${this.quote.id}` : ''; },
  },
  mounted() { this.$nextTick(this.checkOverflow); window.addEventListener('resize', this.checkOverflow); },
  beforeDestroy() { window.removeEventListener('resize', this.checkOverflow); },
  methods: {
    navigateQuote(event) {
      if (this.isEditing || !this.quoteUrl || event?.defaultPrevented) return;
      if (event?.target?.closest?.('a, button, input, select, textarea, label, [role="menu"], [contenteditable="true"]')) return;
      if (window.getSelection?.()?.toString()) return;
      window.location.assign(this.quoteUrl);
    },
    handleCardKeydown(event) {
      if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      if (!event.repeat) this.navigateQuote({ target: event.target });
    },
    resolveBookCover(cover) { return cover || '/images/placeholders/book-cover.svg'; },
    onCoverError(event) {
      const fallback = '/images/placeholders/book-cover.svg';
      if (event?.target && !String(event.target.src || '').includes(fallback)) event.target.src = fallback;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    },
    checkOverflow() {
      if (this.isExpanded) return;
      this.$nextTick(() => { const e = this.$refs.content; this.isOverflowing = Boolean(e && e.scrollHeight > e.clientHeight + 1); });
    },
    toggleExpanded() { this.isExpanded = !this.isExpanded; if (!this.isExpanded) this.$nextTick(this.checkOverflow); },
    startEdit() {
      this.editContent = String(this.quote.content || '');
      this.editPageNumber = this.quote.page_number || null;
      this.editIsPublic = Boolean(this.quote.is_public) && !this.quote.legacy_draft;
      this.editError = '';
      this.isEditing = true;
    },
    cancelEdit() { this.isEditing = false; this.editError = ''; },
    async saveEdit() {
      if (this.isSaving || !this.quote.can_manage) return;
      const content = this.editContent;
      if (!content.trim() || content.length > 1000) {
        this.editError = !content.trim() ? 'Введіть текст цитати.' : 'Цитата повинна містити не більше 1000 символів.';
        return;
      }
      if (this.editPageNumber !== null && this.editPageNumber !== '' && Number(this.editPageNumber) < 1) {
        this.editError = 'Номер сторінки має бути додатним числом.';
        return;
      }
      this.isSaving = true;
      this.editError = '';
      try {
        const response = await axios.put(`/books/${this.quote.book.slug}/quotes/${this.quote.id}`, {
          content, page_number: this.editPageNumber === '' ? null : this.editPageNumber, is_public: this.editIsPublic,
        });
        this.$emit('updated', {
          ...this.quote, ...response.data.quote, content,
          is_public: this.editIsPublic, is_private: !this.editIsPublic, legacy_draft: false,
        });
        this.$emit('notice', 'Цитату оновлено.', 'success');
        this.isEditing = false;
        this.$nextTick(this.checkOverflow);
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        this.editError = errors.content?.[0] || errors.page_number?.[0] || errors.is_public?.[0]
          || error.response?.data?.message || 'Не вдалося оновити цитату.';
      } finally { this.isSaving = false; }
    },
    async deleteQuote() {
      if (this.isDeleting || !this.quote.can_manage) return;
      const context = { ...this.publicationContext, family: 'quote', id: this.quote.id };
      const url = `/books/${this.quote.book.slug}/quotes/${this.quote.id}`;
      this.isDeleting = true;
      let started = false;
      try {
        const confirmed = await confirm('Видалити цю публікацію? Дію неможливо скасувати.', 'Підтвердження', 'warning');
        if (!confirmed || this._isDestroyed) return;
        started = true;
        this.$emit('publication-mutation-start', context);
        const { data } = await axios.delete(url);
        if (data?.success !== true) throw new Error(data?.message || 'Не вдалося видалити публікацію.');
        this.$emit('deleted', context.id, data.quotes_count, context);
        this.$emit('notice', 'Публікацію видалено.', 'success');
      } catch (error) {
        this.$emit('notice', error.response?.data?.message || error.message || 'Не вдалося видалити публікацію.', 'error');
      } finally {
        this.isDeleting = false;
        if (started) {
          if (context.finish) context.finish(context);
          else this.$emit('publication-mutation-finish', context);
        }
      }
    },
    async toggleLike() {
      if (!this.quote.can_like || this.isLikePending) return;
      this.isLikePending = true;
      try {
        const { data } = await axios.post(`/books/${this.quote.book.slug}/quotes/${this.quote.id}/like`);
        this.isLiked = Boolean(data.is_liked); this.likesCount = Number(data.likes_count || 0);
      } catch (error) { this.$emit('notice', error.response?.data?.message || 'Не вдалося змінити вподобання.', 'error'); }
      finally { this.isLikePending = false; }
    },
    async toggleFavorite() {
      if (!this.quote.can_favorite || this.isFavoritePending) return;
      this.isFavoritePending = true;
      try {
        const { data } = await axios.post(`/books/${this.quote.book.slug}/quotes/${this.quote.id}/favorite`);
        this.isFavorited = Boolean(data.is_favorited);
      } catch (error) { this.$emit('notice', error.response?.data?.message || 'Не вдалося змінити збереження.', 'error'); }
      finally { this.isFavoritePending = false; }
    },
  },
};
</script>

<style scoped>
.quote-clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 8;
  overflow: hidden;
}
</style>
